import { message, Upload, type UploadFile } from 'antd'
import { format } from 'date-fns'
import type { Dispatch, SetStateAction } from 'react'
import { GeckoEntry, RolodexEntryPutJson, RolodexEntryType } from 'types'
import { v4 } from 'uuid'

/**
 * Generate a unique document ID
 */
export const generateDocumentId = (): string => v4()

/**
 * Validate file before upload
 */
export const validateFile = (
  file: File,
  maxFileSize: number,
  acceptedFileTypes: ReadonlyArray<string>
): typeof Upload.LIST_IGNORE | false => {
  // Check file size
  const isLtMaxSize = file.size / 1024 / 1024 < maxFileSize
  if (!isLtMaxSize) {
    message.error(`File must be smaller than ${maxFileSize}MB!`)
    return Upload.LIST_IGNORE
  }

  // Check file type if specific types are specified
  if (acceptedFileTypes.length > 0 && !acceptedFileTypes.includes('*/*')) {
    const isValidType = acceptedFileTypes.some((type) => {
      if (type === '*/*') return true
      if (type.includes('*')) {
        const pattern = type.replace(/\*/g, '.*')
        return new RegExp(pattern).test(file.type)
      }
      return file.type === type
    })

    if (!isValidType) {
      message.error(
        `File type not allowed. Accepted types: ${acceptedFileTypes.join(', ')}`
      )
      return Upload.LIST_IGNORE
    }
  }

  return false // Prevent auto upload
}

/**
 * Handle file upload to Rolodex
 */
export const uploadFiles = async (
  fileList: ReadonlyArray<UploadFile>,
  entry: GeckoEntry,
  tags: Record<string, string>,
  rolodexPut: (entries: RolodexEntryPutJson[]) => Promise<unknown>,
  rolodexUploadFile: (
    documentId: string,
    file: File,
    filename: string
  ) => Promise<unknown>,
  onUploadComplete?: (documentIds: ReadonlyArray<string>) => void,
  onUploadError?: (error: string) => void,
  forceRefreshDocuments?: () => Promise<void>,
  setUploadProgress?: Dispatch<SetStateAction<Readonly<Record<string, number>>>>
) => {
  if (fileList.length === 0) {
    message.warning('Please select files to upload')
    return
  }

  const documentIds: Array<string> = []
  const errors: string[] = []

  try {
    await Promise.all(
      fileList.map(async (file, index) => {
        if (!file.originFileObj) return

        const documentId = generateDocumentId()
        documentIds.push(documentId)

        setUploadProgress?.((prev) => ({
          ...prev,
          [file.uid]: ((index + 1) / fileList.length) * 100,
        }))

        try {
          const documentEntryData = {
            id: documentId,
            filename: file.name,
            linkedEntryId: entry.id,
            type: file.name.split('.').pop()?.toLowerCase() || 'unknown',
            uploadDate: format(new Date(), 'yyyy-MM-dd'),
            size: file.originFileObj.size,
          }

          const rolodexEntry: RolodexEntryPutJson = {
            id: documentId,
            type: RolodexEntryType.REPORT,
            data: documentEntryData,
            tags: {
              sandbox: 'sandbox', // TODO remove once perms are added
              ...tags,
            },
            index: {},
          }

          await rolodexPut([rolodexEntry])
          await rolodexUploadFile(documentId, file.originFileObj, file.name)

          message.success(`Successfully uploaded: ${file.name}`)
        } catch (error) {
          const errorMsg = `Failed to upload ${file.name}: ${String(error)}`
          errors.push(errorMsg)
          message.error(errorMsg)
        }
      })
    )

    if (errors.length === 0) {
      onUploadComplete?.(documentIds)
      await forceRefreshDocuments?.()
    } else {
      onUploadError?.(`${errors.length} file(s) failed to upload`)
    }
  } catch (error) {
    const errorMsg = `Upload failed: ${String(error)}`
    message.error(errorMsg)
    onUploadError?.(errorMsg)
  }
}
